/**
 * BATON.ts
 * Bridge And Transport Operations Notation
 * 
 * THE SINGLE SOURCE OF TRUTH FOR ALL MAESTRO HANDLER COMMUNICATION
 * 
 * Every handler imports this file. Every UI component imports this file.
 * Change this file once → all handlers and UI inherit the change.
 * 
 * Version: 1.0.0
 * Protocol Version: 1
 */

// =============================================================================
// PROTOCOL VERSION
// =============================================================================

export const BATON_VERSION = '1.0.0';
export const PROTOCOL_VERSION = 1;
export const PROTOCOL_VERSION_MIN = 1;

// =============================================================================
// HANDLER IDENTIFIERS
// =============================================================================

/** Device/Protocol Handlers */
export type DeviceHandlerId =
  | 'upnp'
  | 'chromecast'
  | 'sonos'
  | 'roon';

/** Streaming Service Handlers */
export type ServiceHandlerId =
  | 'qobuz'
  | 'applemusic'
  | 'amazonmusic';

/** Content Handlers */
export type ContentHandlerId =
  | 'metadata'
  | 'browse'
  | 'search'
  | 'library'
  | 'local';

/** System Handlers */
export type SystemHandlerId =
  | 'auth'
  | 'subscription'
  | 'entitlement'
  | 'sync'
  | 'registry'
  | 'config'
  | 'diagnostics';

/** Bridge Handlers */
export type BridgeHandlerId =
  | 'bridge.control'
  | 'bridge.remote'
  | 'bridge.stream';

/** Local Runtime Handlers (browser-based) */
export type LocalHandlerId =
  | 'transport'
  | 'queue'
  | 'volume'
  | 'view';

/** All Handler IDs */
export type HandlerId =
  | DeviceHandlerId
  | ServiceHandlerId
  | ContentHandlerId
  | SystemHandlerId
  | BridgeHandlerId
  | LocalHandlerId;

// =============================================================================
// HANDLER LOCATION
// =============================================================================

export type HandlerLocation = 'cloud' | 'local' | 'bridge';

// =============================================================================
// MESSAGE TYPES
// =============================================================================

export type MessageType = 'command' | 'response' | 'event';

// =============================================================================
// MESSAGE ENVELOPE
// =============================================================================

export interface BatonMessage<T = unknown> {
  /** Protocol version */
  v: number;
  /** Unique message ID */
  id: string;
  /** Timestamp (ms since epoch) */
  ts: number;
  /** Message type */
  type: MessageType;
  /** Target/source handler */
  handler: HandlerId;
  /** Handler location (optional, for routing) */
  location?: HandlerLocation;
  /** Auth context (optional) */
  auth?: AuthContext;
  /** Device context (optional) */
  device?: DeviceContext;
  /** Type-specific payload */
  payload: T;
}

// =============================================================================
// AUTHENTICATION CONTEXT
// =============================================================================

export interface AuthContext {
  /** User ID */
  userId: string;
  /** Session token */
  token: string;
  /** Token expiry */
  expiresAt: number;
}

// =============================================================================
// DEVICE CONTEXT
// =============================================================================

export type Platform = 'ios' | 'android' | 'web' | 'macos' | 'windows';
export type FormFactor = 'phone' | 'tablet' | 'foldable' | 'desktop';
export type PerformanceTier = 'low' | 'standard' | 'high';
export type NetworkType = 'wifi' | 'cellular' | 'ethernet' | 'unknown';

export interface DeviceContext {
  platform: Platform;
  formFactor: FormFactor;
  tier: PerformanceTier;
  networkType: NetworkType;
  networkMetered: boolean;
  locale: string;
  timezone: string;
  appVersion: string;
}

// =============================================================================
// COMMAND TYPES
// =============================================================================

export type CommandType =
  // Lua
  | 'init'
  | 'shutdown'
  | 'getCapabilities'
  | 'getVersion'
  | 'healthCheck'
  // Discovery
  | 'discover'
  | 'connect'
  | 'disconnect'
  | 'status'
  | 'getDevices'
  // Transport
  | 'play'
  | 'pause'
  | 'stop'
  | 'seek'
  | 'next'
  | 'previous'
  | 'setPlayMode'
  // Volume
  | 'setVolume'
  | 'getVolume'
  | 'mute'
  | 'unmute'
  // Queue
  | 'getQueue'
  | 'queueAdd'
  | 'queueRemove'
  | 'queueClear'
  | 'queueReorder'
  | 'queueJump'
  // Content
  | 'browse'
  | 'search'
  | 'getTrack'
  | 'getAlbum'
  | 'getArtist'
  | 'getPlaylist'
  | 'getCredits'
  | 'getStreamUrl'
  // Auth & Subscription
  | 'authenticate'
  | 'logout'
  | 'refreshToken'
  | 'getSubscription'
  | 'checkEntitlement'
  // Library
  | 'addToLibrary'
  | 'removeFromLibrary'
  | 'getLibrary'
  // Remote Library Access
  | 'requestLocalFile'
  | 'registerManifest'
  // Sync
  | 'syncState'
  | 'getState'
  // View (UI State Sync)
  | 'navigate'
  | 'select'
  | 'deselect'
  // Metadata (Rich content)
  | 'getArtistMetadata'
  | 'getAlbumMetadata'
  | 'getTrackMetadata'
  | 'getPersonMetadata';

// =============================================================================
// EVENT TYPES
// =============================================================================

export type EventType =
  // Device
  | 'deviceDiscovered'
  | 'deviceLost'
  | 'deviceUpdated'
  | 'connectionChanged'
  // Playback
  | 'playbackStateChanged'
  | 'positionChanged'
  | 'trackChanged'
  | 'queueChanged'
  | 'playModeChanged'
  // Volume
  | 'volumeChanged'
  | 'muteChanged'
  // System
  | 'handlerReady'
  | 'handlerUpdated'
  | 'handlerError'
  | 'syncStateChanged'
  | 'entitlementChanged'
  | 'networkChanged'
  // Subscription
  | 'subscriptionChanged'
  | 'trialExpiring'
  | 'paymentFailed'
  // View (UI State Sync)
  | 'viewChanged'
  | 'selectionChanged'
  // Metadata (Rich content)
  | 'artistMetadataLoaded'
  | 'albumMetadataLoaded'
  | 'trackMetadataLoaded'
  | 'personMetadataLoaded'
  // Remote Library Access
  | 'localFileReady'
  | 'localFileError'
  | 'manifestRegistered';

// =============================================================================
// PLAYBACK STATES
// =============================================================================

export type PlaybackState =
  | 'idle'
  | 'playing'
  | 'paused'
  | 'buffering'
  | 'stopped'
  | 'error';

export type ConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error';

export type PlayMode = {
  shuffle: boolean;
  repeat: 'off' | 'one' | 'all';
};

// =============================================================================
// SUBSCRIPTION & ENTITLEMENT
// =============================================================================

export type SubscriptionStatus =
  | 'trial'
  | 'active'
  | 'grace'
  | 'expired'
  | 'none';

export interface SubscriptionState {
  status: SubscriptionStatus;
  plan: string;
  trialEndsAt?: number;
  renewsAt?: number;
  features: string[];
}

export interface EntitlementCheck {
  feature: string;
  granted: boolean;
  reason?: string;
  upgradeUrl?: string;
}

// =============================================================================
// DEVICE TYPES
// =============================================================================

export type DeviceType =
  | 'renderer'
  | 'media_server'
  | 'streaming'
  | 'group'
  | 'bridge'
  | 'unknown';

export interface DeviceCapabilities {
  canPlay: boolean;
  canPause: boolean;
  canStop: boolean;
  canSeek: boolean;
  canNext: boolean;
  canPrevious: boolean;
  canQueue: boolean;
  canVolume: boolean;
  canMute: boolean;
  canGapless: boolean;
  canGroup: boolean;
  supportedFormats?: string[];
  maxSampleRate?: number;
  maxBitDepth?: number;
}

export interface Device {
  id: string;
  name: string;
  type: DeviceType;
  handler: HandlerId;
  state: ConnectionState;
  capabilities: DeviceCapabilities;
  location: HandlerLocation;
  metadata?: {
    manufacturer?: string;
    model?: string;
    firmwareVersion?: string;
    ipAddress?: string;
    roomName?: string;
  };
}

// =============================================================================
// CONTENT TYPES
// =============================================================================

export interface MetadataProvenance {
  source: 'musicbrainz' | 'service' | 'local' | 'ai' | 'manual';
  confidence: number;
  verifiedAt?: number;
  citations?: string[];
  manualOverride: boolean;
}

export interface TrackMetadata {
  id: string;
  title: string;
  artist: string;
  artistId?: string;
  album?: string;
  albumId?: string;
  albumArtist?: string;
  duration?: number;
  trackNumber?: number;
  discNumber?: number;
  year?: number;
  genre?: string;
  artworkUrl?: string;
  artworkUrlHiRes?: string;
  format?: string;
  sampleRate?: number;
  bitDepth?: number;
  source: HandlerId | 'local_library';
  provenance?: MetadataProvenance;
  
  // CRITICAL: Stream URL for network renderer playback
  // Without this, UPnP devices can't play the track (error 701)
  streamUrl?: string;

  /** Absolute file path for local library tracks (resolved via BATON tunnel). */
  file_path?: string;

  /** Connector machine that owns this file (for remote playback routing). */
  machine_id?: string;

  /** Qobuz track ID (undefined for local-only tracks). */
  qobuzTrackId?: string;
  /** Generic service track ID. */
  serviceTrackId?: string;
}

export interface AlbumMetadata {
  id: string;
  title: string;
  artist: string;
  artistId?: string;
  year?: number;
  genre?: string;
  trackCount?: number;
  discCount?: number;
  artworkUrl?: string;
  artworkUrlHiRes?: string;
  description?: string;
  source: HandlerId;
  provenance?: MetadataProvenance;
}

export interface ArtistMetadata {
  id: string;
  name: string;
  bio?: string;
  genres?: string[];
  artworkUrl?: string;
  source: HandlerId;
  provenance?: MetadataProvenance;
}

export interface Credit {
  personId: string;
  personName: string;
  role: string;
  instrument?: string;
}

export interface QueueTrack {
  queueId: string;
  track: TrackMetadata;
  addedAt: number;
  source: 'user' | 'radio' | 'ai';
}

// =============================================================================
// HANDLER CAPABILITIES
// =============================================================================

export interface HandlerCapabilities {
  handlerId: HandlerId;
  version: string;
  protocolVersion: number;
  location: HandlerLocation;
  
  capabilities: {
    discovery: boolean;
    transport: CommandType[];
    volume: boolean;
    queue: boolean;
    gapless: boolean;
    grouping: boolean;
    search: boolean;
    authentication: boolean;
    offline: boolean;
  };
  
  deviceTypes: DeviceType[];
  
  requirements: {
    subscription: 'none' | 'trial' | 'active';
    networkAccess: 'local' | 'remote' | 'any';
    minAppVersion?: string;
  };
}

// =============================================================================
// COMMAND PAYLOADS
// =============================================================================

export interface InitCommand {
  cmd: 'init';
  protocolVersion: number;
  config?: Record<string, unknown>;
  device?: DeviceContext;
  /** Per-client-instance identifier (tab/app instance). Used to prevent same-origin echo loops. */
  clientInstanceId?: string;
}

export interface DiscoverCommand {
  cmd: 'discover';
  timeout?: number;
  deviceTypes?: DeviceType[];
}

export interface ConnectCommand {
  cmd: 'connect';
  deviceId: string;
  options?: Record<string, unknown>;
}

export interface PlayCommand {
  cmd: 'play';
  trackId?: string;
  albumId?: string;
  playlistId?: string;
  streamUrl?: string;
  position?: number;
  queue?: boolean;
}

export interface SeekCommand {
  cmd: 'seek';
  position: number;
}

export interface SetVolumeCommand {
  cmd: 'setVolume';
  level: number;
  deviceId?: string;
}

export interface SetPlayModeCommand {
  cmd: 'setPlayMode';
  shuffle?: boolean;
  repeat?: 'off' | 'one' | 'all';
}

export interface QueueAddCommand {
  cmd: 'queueAdd';
  tracks: Array<{ trackId: string; source: HandlerId }>;
  position?: number;
}

export interface QueueRemoveCommand {
  cmd: 'queueRemove';
  indices: number[];
}

export interface QueueReorderCommand {
  cmd: 'queueReorder';
  fromIndex: number;
  toIndex: number;
}

export interface QueueJumpCommand {
  cmd: 'queueJump';
  index: number;
}

export interface BrowseCommand {
  cmd: 'browse';
  path: string;
  page?: number;
  limit?: number;
}

export interface SearchCommand {
  cmd: 'search';
  query: string;
  types?: ('track' | 'album' | 'artist' | 'playlist')[];
  limit?: number;
  source?: HandlerId;
}

export interface AuthenticateCommand {
  cmd: 'authenticate';
  provider?: string;
  credentials?: {
    token?: string;
    refreshToken?: string;
    username?: string;
    password?: string;
  };
}

export interface CheckEntitlementCommand {
  cmd: 'checkEntitlement';
  feature: string;
}

// =============================================================================
// VIEW COMMAND PAYLOADS (UI State Sync)
// =============================================================================

export interface NavigateCommand {
  cmd: 'navigate';
  screen: string;
  params?: Record<string, unknown>;
  /** Per-client-instance identifier (tab/app instance). Used to prevent same-origin echo loops. */
  originClientInstanceId?: string;
}

export type ViewItemType = 'track' | 'album' | 'artist';

export interface SelectCommand {
  cmd: 'select';
  itemType: ViewItemType;
  index?: number;
  itemId?: string;
  context?: string;
  /** Per-client-instance identifier (tab/app instance). Used to prevent same-origin echo loops. */
  originClientInstanceId?: string;
}

export interface DeselectCommand {
  cmd: 'deselect';
  itemType: ViewItemType;
  context?: string;
  /** Per-client-instance identifier (tab/app instance). Used to prevent same-origin echo loops. */
  originClientInstanceId?: string;
}

// =============================================================================
// METADATA COMMAND PAYLOADS (Rich Content)
// =============================================================================

export interface GetArtistMetadataCommand {
  cmd: 'getArtistMetadata';
  artistId: string;
  artistName?: string;
}

export interface GetAlbumMetadataCommand {
  cmd: 'getAlbumMetadata';
  albumId: string;
  albumTitle?: string;
}

export interface GetTrackMetadataCommand {
  cmd: 'getTrackMetadata';
  trackId: string;
}

export interface GetPersonMetadataCommand {
  cmd: 'getPersonMetadata';
  personId: string;
  personName?: string;
}

// =============================================================================
// REMOTE LIBRARY ACCESS COMMAND PAYLOADS
// =============================================================================

export interface RequestLocalFileCommand {
  cmd: 'requestLocalFile';
  requestId: string;
  filePath: string;
  userId: string;
  /** Identifies which Connector machine holds the file (hostname-uuid). */
  machineId?: string;
  /** Track ID for correlation with localFileReady/localFileError responses. */
  trackId?: string;
}

export interface RegisterManifestCommand {
  cmd: 'registerManifest';
  userId: string;
  trackCount: number;
}

// =============================================================================
// REMOTE LIBRARY ACCESS EVENT PAYLOADS
// =============================================================================

export interface LocalFileReadyEvent {
  event: 'localFileReady';
  requestId: string;
  url: string;
  expiresAt: number;
}

export interface LocalFileErrorEvent {
  event: 'localFileError';
  requestId: string;
  error: string;
}

export type CommandPayload =
  | InitCommand
  | DiscoverCommand
  | ConnectCommand
  | PlayCommand
  | SeekCommand
  | SetVolumeCommand
  | SetPlayModeCommand
  | QueueAddCommand
  | QueueRemoveCommand
  | QueueReorderCommand
  | QueueJumpCommand
  | BrowseCommand
  | SearchCommand
  | AuthenticateCommand
  | CheckEntitlementCommand
  | NavigateCommand
  | SelectCommand
  | DeselectCommand
  | GetArtistMetadataCommand
  | GetAlbumMetadataCommand
  | GetTrackMetadataCommand
  | GetPersonMetadataCommand
  | RequestLocalFileCommand
  | RegisterManifestCommand
  | { cmd: CommandType };

// =============================================================================
// EVENT PAYLOADS
// =============================================================================

export interface DeviceDiscoveredEvent {
  event: 'deviceDiscovered';
  device: Device;
}

export interface DeviceLostEvent {
  event: 'deviceLost';
  deviceId: string;
}

export interface ConnectionChangedEvent {
  event: 'connectionChanged';
  deviceId: string;
  state: ConnectionState;
  error?: ErrorInfo;
}

export interface PlaybackStateChangedEvent {
  event: 'playbackStateChanged';
  state: PlaybackState;
  deviceId?: string;
}

export interface PositionChangedEvent {
  event: 'positionChanged';
  position: number;
  duration: number;
  deviceId?: string;
}

export interface TrackChangedEvent {
  event: 'trackChanged';
  track: TrackMetadata | null;
  deviceId?: string;
}

export interface VolumeChangedEvent {
  event: 'volumeChanged';
  level: number;
  muted: boolean;
  deviceId?: string;
}

export interface QueueChangedEvent {
  event: 'queueChanged';
  queue: QueueTrack[];
  currentIndex: number;
}

export interface PlayModeChangedEvent {
  event: 'playModeChanged';
  mode: PlayMode;
}

export interface HandlerReadyEvent {
  event: 'handlerReady';
  capabilities: HandlerCapabilities;
}

export interface HandlerUpdatedEvent {
  event: 'handlerUpdated';
  previousVersion: string;
  newVersion: string;
}

export interface EntitlementChangedEvent {
  event: 'entitlementChanged';
  subscription: SubscriptionState;
}

export interface SubscriptionChangedEvent {
  event: 'subscriptionChanged';
  subscription: SubscriptionState;
}

export interface HandlerErrorEvent {
  event: 'handlerError';
  error: ErrorInfo;
}

// =============================================================================
// VIEW EVENT PAYLOADS (UI State Sync)
// =============================================================================

export interface ViewChangedEvent {
  event: 'viewChanged';
  screen: string;
  params?: Record<string, unknown>;
  sourceDeviceId: string;
}

export interface SelectionChangedEvent {
  event: 'selectionChanged';
  itemType: ViewItemType;
  index?: number;
  itemId?: string;
  context?: string;
  sourceDeviceId: string;
}

export type EventPayload =
  | DeviceDiscoveredEvent
  | DeviceLostEvent
  | ConnectionChangedEvent
  | PlaybackStateChangedEvent
  | PositionChangedEvent
  | TrackChangedEvent
  | VolumeChangedEvent
  | QueueChangedEvent
  | PlayModeChangedEvent
  | HandlerReadyEvent
  | HandlerUpdatedEvent
  | EntitlementChangedEvent
  | SubscriptionChangedEvent
  | HandlerErrorEvent
  | ViewChangedEvent
  | SelectionChangedEvent
  | LocalFileReadyEvent
  | LocalFileErrorEvent
  | { event: EventType };

// =============================================================================
// RESPONSE PAYLOADS
// =============================================================================

export interface SuccessResponse<T = unknown> {
  success: true;
  data?: T;
}

export interface ErrorResponse {
  success: false;
  error: ErrorInfo;
}

export type ResponsePayload<T = unknown> = SuccessResponse<T> | ErrorResponse;

// =============================================================================
// ERROR HANDLING
// =============================================================================

export type ErrorCode =
  // 1xxx - Connection
  | 1001 | 1002 | 1003 | 1004 | 1005 | 1006 | 1007
  // 2xxx - Transport
  | 2001 | 2002 | 2003 | 2004 | 2005 | 2006
  // 3xxx - Queue
  | 3001 | 3002 | 3003
  // 4xxx - Handler
  | 4001 | 4002 | 4003 | 4004 | 4005 | 4006
  // 5xxx - Protocol
  | 5001 | 5002 | 5003 | 5004 | 5005
  // 6xxx - Service
  | 6001 | 6002 | 6003 | 6004 | 6005
  // 7xxx - Entitlement
  | 7001 | 7002 | 7003 | 7004 | 7005;

export interface ErrorInfo {
  code: ErrorCode;
  message: string;
  details?: Record<string, unknown>;
  recoverable: boolean;
  suggestedAction?: 'retry' | 'reconnect' | 'reauthenticate' | 'update' | 'upgrade' | 'none';
  upgradeUrl?: string;
}

export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  // Connection
  1001: 'Device not found',
  1002: 'Connection refused',
  1003: 'Connection lost',
  1004: 'Authentication failed',
  1005: 'Network unreachable',
  1006: 'Connection timeout',
  1007: 'Remote access unavailable',
  // Transport
  2001: 'Nothing to play',
  2002: 'Format not supported',
  2003: 'Seek not supported',
  2004: 'Track not available',
  2005: 'Playback failed',
  2006: 'DRM restriction',
  // Queue
  3001: 'Queue is empty',
  3002: 'Invalid queue position',
  3003: 'Queue limit exceeded',
  // Handler
  4001: 'Handler not initialized',
  4002: 'Capability not supported',
  4003: 'Handler is busy',
  4004: 'Handler update required',
  4005: 'Handler crashed',
  4006: 'Handler not found',
  // Protocol
  5001: 'Invalid message format',
  5002: 'Unknown command',
  5003: 'Protocol version mismatch',
  5004: 'Invalid payload',
  5005: 'Message too large',
  // Service
  6001: 'Service unavailable',
  6002: 'Rate limited',
  6003: 'Subscription required',
  6004: 'Region restricted',
  6005: 'Service authentication failed',
  // Entitlement
  7001: 'Trial expired',
  7002: 'Subscription expired',
  7003: 'Payment failed',
  7004: 'Feature not in plan',
  7005: 'Remote access disabled',
};

// =============================================================================
// MESSAGE FACTORY FUNCTIONS
// =============================================================================

let messageCounter = 0;

function generateMessageId(): string {
  return `baton_${Date.now()}_${++messageCounter}`;
}

/**
 * Create a command message
 */
export function createCommand<T extends CommandPayload>(
  handler: HandlerId,
  payload: T,
  options?: {
    location?: HandlerLocation;
    auth?: AuthContext;
    device?: DeviceContext;
  }
): BatonMessage<T> {
  return {
    v: PROTOCOL_VERSION,
    id: generateMessageId(),
    ts: Date.now(),
    type: 'command',
    handler,
    location: options?.location,
    auth: options?.auth,
    device: options?.device,
    payload,
  };
}

/**
 * Create a response message
 */
export function createResponse<T>(
  originalMessage: BatonMessage,
  payload: ResponsePayload<T>
): BatonMessage<ResponsePayload<T>> {
  return {
    v: PROTOCOL_VERSION,
    id: originalMessage.id,
    ts: Date.now(),
    type: 'response',
    handler: originalMessage.handler,
    payload,
  };
}

/**
 * Create a success response
 */
export function createSuccessResponse<T>(
  originalMessage: BatonMessage,
  data?: T
): BatonMessage<SuccessResponse<T>> {
  return createResponse(originalMessage, { success: true, data }) as BatonMessage<SuccessResponse<T>>;
}

/**
 * Create an error response
 */
export function createErrorResponse(
  originalMessage: BatonMessage,
  code: ErrorCode,
  options?: {
    details?: Record<string, unknown>;
    recoverable?: boolean;
    suggestedAction?: ErrorInfo['suggestedAction'];
    upgradeUrl?: string;
  }
): BatonMessage<ErrorResponse> {
  return createResponse(originalMessage, {
    success: false,
    error: {
      code,
      message: ERROR_MESSAGES[code] || 'Unknown error',
      details: options?.details,
      recoverable: options?.recoverable ?? true,
      suggestedAction: options?.suggestedAction,
      upgradeUrl: options?.upgradeUrl,
    },
  }) as BatonMessage<ErrorResponse>;
}

/**
 * Create an event message
 */
export function createEvent<T extends EventPayload>(
  handler: HandlerId,
  payload: T,
  location?: HandlerLocation
): BatonMessage<T> {
  return {
    v: PROTOCOL_VERSION,
    id: generateMessageId(),
    ts: Date.now(),
    type: 'event',
    handler,
    location,
    payload,
  };
}

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

export function validateMessage(msg: unknown): msg is BatonMessage {
  if (typeof msg !== 'object' || msg === null) return false;
  const m = msg as Record<string, unknown>;
  return (
    typeof m.v === 'number' &&
    typeof m.id === 'string' &&
    typeof m.ts === 'number' &&
    ['command', 'response', 'event'].includes(m.type as string) &&
    typeof m.handler === 'string' &&
    m.payload !== undefined
  );
}

export function isSuccess<T>(payload: ResponsePayload<T>): payload is SuccessResponse<T> {
  return payload.success === true;
}

export function isError(payload: ResponsePayload): payload is ErrorResponse {
  return payload.success === false;
}

export function isCompatibleVersion(version: number): boolean {
  return version >= PROTOCOL_VERSION_MIN && version <= PROTOCOL_VERSION;
}

export function isEntitlementError(code: ErrorCode): boolean {
  return code >= 7001 && code <= 7005;
}

export function isRecoverableError(code: ErrorCode): boolean {
  return ![4005, 5003, 7001, 7002].includes(code);
}

// =============================================================================
// HANDLER BASE INTERFACE
// =============================================================================

/**
 * Every handler MUST implement this interface
 */
export interface BatonHandler {
  readonly handlerId: HandlerId;
  readonly location: HandlerLocation;
  
  init(config?: Record<string, unknown>): Promise<HandlerCapabilities>;
  handleCommand(message: BatonMessage<CommandPayload>): Promise<BatonMessage<ResponsePayload>>;
  onEvent(callback: (event: BatonMessage<EventPayload>) => void): () => void;
  shutdown(): Promise<void>;
}

// =============================================================================
// TYPE HELPERS
// =============================================================================

export type TypedCommand<T extends CommandPayload> = BatonMessage<T>;
export type TypedEvent<T extends EventPayload> = BatonMessage<T>;
export type TypedResponse<T> = BatonMessage<ResponsePayload<T>>;

